import { ReactNode } from "react";
import Utils from "../../utils";
import BigNumber from "bignumber.js";
import {
  OlympusGloryBallResult,
  OlympusGloryBingoCard,
  OlympusGloryBingoReward,
  OlympusGloryBingoRewardTypeEnum,
  OlympusGloryBingoSymbol,
  OlympusGloryBingoSymbolEnum,
  OlympusGloryMultiplierEnum,
  OlympusGloryPin,
  OlympusGloryPinTypeEnum,
} from "./types";

const OlympusGloryUtils = {
  gate(
    pin: OlympusGloryPin,
    previousPin: OlympusGloryPin,
    classNames: string[] = []
  ): ReactNode {
    classNames.push("gate");
    if (Number.isNaN(pin.type) || Number.isNaN(pin.multiplier)) return "";
    switch (pin.type) {
      case OlympusGloryPinTypeEnum.SkullPin:
        return (
          <img className={classNames.join(" ")} alt="Lose" src="/og/nm0.png" />
        );
      case OlympusGloryPinTypeEnum.MultiplierPin:
        switch (pin.multiplier) {
          case OlympusGloryMultiplierEnum.Multiplier1:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x1"
                src="/og/nm1.png"
              />
            );
          case OlympusGloryMultiplierEnum.Multiplier2:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x2"
                src="/og/nm2.png"
              />
            );
          case OlympusGloryMultiplierEnum.Multiplier4:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x4"
                src="/og/nm4.png"
              />
            );
          case OlympusGloryMultiplierEnum.Multiplier6:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x6"
                src="/og/nm6.png"
              />
            );
          case OlympusGloryMultiplierEnum.Multiplier8:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x8"
                src="/og/nm8.png"
              />
            );
          case OlympusGloryMultiplierEnum.Multiplier10:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x10"
                src="/og/nm10.png"
              />
            );
          case OlympusGloryMultiplierEnum.Multiplier12:
            return (
              <img
                className={classNames.join(" ")}
                alt="Multiplier x12"
                src="/og/nm12.png"
              />
            );
          default:
            return pin.type;
        }
      case OlympusGloryPinTypeEnum.SymbolPin:
        switch (previousPin.bingoSymbol) {
          case OlympusGloryBingoSymbolEnum.BingoSymbolTrident:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n201.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolPlutoBident:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n202.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolFlower:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n203.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolThunder:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n204.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolHolyGrail:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n205.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolBowArrow:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n206.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolHarp:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n207.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolHammers:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n208.png"
                />
              </div>
            );
          case OlympusGloryBingoSymbolEnum.BingoSymbolSnakes:
            return (
              <div className={"tab_bg"}>
                <img
                  className={classNames.join(" ")}
                  alt="Symbol"
                  src="/og/n209.png"
                />
              </div>
            );
          default:
            return pin.type;
        }
      default:
        return pin.type;
    }
  },
  gateSummary(
    results: OlympusGloryBallResult[]
  ): { pin: OlympusGloryPin; value: number }[] {
    results.forEach((r) =>
      Object.keys(r.pins).forEach((key) => {
        r.pins[Number(key)].position = 0;
        if (r.exchangePinFrom != null) r.exchangePinFrom.position = 0;
      })
    );
    const summary = Array.from(
      results.reduce(
        (acc, r) =>
          acc.set(
            JSON.stringify(
              r.pins[r.resultPin].type == OlympusGloryPinTypeEnum.SymbolPin
                ? r.exchangePinFrom
                : r.pins[r.resultPin]
            ),
            (acc.get(
              JSON.stringify(
                r.pins[r.resultPin].type == OlympusGloryPinTypeEnum.SymbolPin
                  ? r.exchangePinFrom
                  : r.pins[r.resultPin]
              )
            ) || 0) + 1
          ),
        new Map<string, number>()
      ),
      ([pin, value]) => ({ pin, value })
    );
    const data = summary.map((r) => ({
      pin: JSON.parse(r.pin) as OlympusGloryPin,
      value: r.value,
    }));
    return data.sort((a, b) => {
      if (a.pin.type === b.pin.type && a.pin.multiplier === b.pin.multiplier) {
        return a.pin.bingoSymbol - b.pin.bingoSymbol;
      }
      if (a.pin.type === b.pin.type) {
        return a.pin.multiplier - b.pin.multiplier;
      }
      return a.pin.type - b.pin.type;
    });
  },
  bingoReward(
    reward: OlympusGloryBingoReward,
    classNames: string[] = []
  ): ReactNode {
    if (Number.isNaN(reward.type)) return "";
    classNames.push("fw-bold", "m-0");
    switch (reward.type) {
      case OlympusGloryBingoRewardTypeEnum.MultiplierReward:
        return <p className={classNames.join(" ")}>x{reward.multiplier}</p>;
      case OlympusGloryBingoRewardTypeEnum.ExtraBallsReward:
        return (
          <p className={classNames.join(" ")}>Extra Balls {reward.freeBalls}</p>
        );
      default:
        return reward.type;
    }
  },
  bingoWin(
    reward: OlympusGloryBingoReward,
    lineAmount: number,
    classNames: string[] = []
  ): ReactNode {
    if (Number.isNaN(reward.type) || Number.isNaN(lineAmount)) return "";
    classNames.push("fw-bold", "m-0");
    switch (reward.type) {
      case OlympusGloryBingoRewardTypeEnum.MultiplierReward:
        return (
          <p className={classNames.join(" ")}>
            {Utils.asCurrency(
              BigNumber(lineAmount)
                .multipliedBy(BigNumber(reward.multiplier))
                .toNumber()
            )}
          </p>
        );
      case OlympusGloryBingoRewardTypeEnum.ExtraBallsReward:
        return "-";
      default:
        return reward.type;
    }
  },
  bingoSymbol(
    symbol: OlympusGloryBingoSymbol,
    alwaysActivated = true,
    classNames: string[] = []
  ): ReactNode {
    if (Number.isNaN(symbol.symbolEnum)) return "";
    classNames.push("symbol");
    switch (symbol.symbolEnum) {
      case OlympusGloryBingoSymbolEnum.BingoSymbolTrident:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns201.png"
                  : "/og/ns201_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolPlutoBident:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns202.png"
                  : "/og/ns202_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolFlower:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns203.png"
                  : "/og/ns203_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolThunder:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns204.png"
                  : "/og/ns204_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolHolyGrail:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns205.png"
                  : "/og/ns205_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolBowArrow:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns206.png"
                  : "/og/ns206_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolHarp:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns207.png"
                  : "/og/ns207_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolHammers:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns208.png"
                  : "/og/ns208_dark.png"
              }
            />
          </div>
        );
      case OlympusGloryBingoSymbolEnum.BingoSymbolSnakes:
        return (
          <div className={classNames.join(" ").concat(" bg")}>
            <img
              className={classNames.join(" ")}
              alt="Symbol"
              src={
                symbol.activated || alwaysActivated
                  ? "/og/ns209.png"
                  : "/og/ns209_dark.png"
              }
            />
          </div>
        );
      default:
        return symbol.symbolEnum;
    }
  },
  bingoCard(
    bingo: OlympusGloryBingoCard,
    classNames: string[] = []
  ): ReactNode {
    return (
      <div className="d-flex justify-content-start">
        <div className="ms-2">
          {bingo.symbols.map((row, i) => (
            <div key={i} className="d-flex">
              {row.map((cell) => (
                <span key={cell.symbolEnum}>
                  {this.bingoSymbol(cell, false, classNames)}
                </span>
              ))}
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-evenly align-items-start flex-column">
          <>
            {bingo.symbols.map((row, i) => (
              <div key={i} className="ms-2 mb-0">
                {this.bingoReward(bingo.rewards[i])}
              </div>
            ))}
          </>
        </div>
      </div>
    );
  },
  maxBetFormular(amount: number | BigNumber): BigNumber {
    return BigNumber(amount);
  },
  maxPayoutFormular(amount: number | BigNumber): BigNumber {
    return BigNumber(amount).multipliedBy(300);
  },
} as const;

export default OlympusGloryUtils;
